import type { StreamSessionDoc } from './timing'
/**
 * Which of a weekend's timing sessions belong to this team, and which one to
 * show by default.
 *
 * A meeting produces far more timing than any one team wants. Fun Cup, Belcar
 * and the Ligier JS Cup share a paddock and sometimes share a channel, and a
 * team that ran only Fun Cup should not have to look at the other two — the
 * whole point of the dashboard is that it opens on your race.
 *
 * The only inclusion test is the series name. That is a weaker signal than it
 * sounds — the stream has been observed renaming its own championship
 * mid-afternoon — so there are two supports for it. The team may confirm which
 * stream strings are theirs, which then wins outright; and the numbers that
 * actually appeared in each session are recorded, so a failed match can be
 * resolved from evidence rather than guesswork.
 *
 * Car presence is deliberately NOT an inclusion rule. Numbers repeat across
 * series at one meeting — a Ligier 436 and a Fun Cup 436 are ordinary — so
 * "our number is in this session" is not proof, and treating it as proof would
 * do worse than show a spurious row: it would default a dashboard to another
 * team's car and say nothing about it.
 *
 * None of this is a security boundary. All derived timing is readable by any
 * signed-in principal, so hiding the wrong session costs a click, not access.
 * That is what makes it safe to hide aggressively.
 */

/** What relevance needs from an entry. */
export interface EntryLike {
  /** The number this car carries at this event. */
  number: string
  /** The series as the team names it. Free text: nobody publishes a registry. */
  series?: string
  /**
   * The stream's own series strings, once somebody has confirmed them.
   *
   * Authoritative when present — a confirmation narrows, so the fuzzy fallback
   * is skipped entirely. Otherwise confirming "Fun Cup Belgium" would still
   * drag in "Fun Cup France", and the confirmation would have achieved nothing.
   */
  seriesMatched?: string[]
  /**
   * The channel this entry's timing is read from, as the event's
   * administrator set it for the entry's series.
   *
   * Required for the entry to claim anything. There is no default and no
   * inference: a series without a channel shows nothing, which says plainly
   * that it has not been set up, where a guess showed the same race twice.
   */
  channel?: string | null
}

/**
 * Another series the event has a channel for, which the team is not entered in.
 *
 * Offered below the team's own sessions, so a race worth watching is one click
 * away without the picker filling up with every bucket the capture produced.
 */
export interface ConfiguredSeries {
  channel: string
  /** What the stream calls the series, from the Series page. */
  names: string[]
}

/** What relevance needs from a timing bucket. */
/**
 * What the relevance logic needs to know about a session.
 *
 * Derived from the stored document rather than declared beside it, so a field
 * added to one cannot be forgotten on the other — carsWithPitData had to be
 * added in two places, and that is the whole argument. The fields it picks are
 * evidence for "is this ours" and "which of these is fullest"; the timestamps
 * are flattened to milliseconds so this module stays free of Firestore types.
 */
export type SessionLike = Pick<
  StreamSessionDoc,
  'seriesNameRaw' | 'sessionNameRaw' | 'cars' | 'carsWithPitData'
> & {
  id: string
  /** Milliseconds since the epoch, or null if the bucket has never reported. */
  lastSeenMs: number | null
}

/**
 * Reduces a series name to something comparable.
 *
 * Punctuation and spacing are where vendors differ most — "FUN CUP BELGIUM",
 * "Fun Cup Belgium" and "FunCup-Belgium" are one championship — while the word
 * order and the words themselves are stable enough to rely on.
 */
export function normaliseSeries(s: string): string {
  return s.toLowerCase().replace(/[^a-z0-9]/g, '')
}

/**
 * The shortest normalised name allowed to match as a prefix or substring.
 *
 * "Fun Cup" should find "Fun Cup Belgium", which needs containment rather than
 * equality. But containment with no floor makes "Cup" match every championship
 * at the meeting, so a team that typed something vague would see everything and
 * conclude the filter was broken.
 */
const minPartialLength = 6

/** Whether two series names refer to the same championship, as best we can tell. */
export function seriesLooksSame(a: string, b: string): boolean {
  const x = normaliseSeries(a)
  const y = normaliseSeries(b)
  if (!x || !y) return false
  if (x === y) return true
  if (x.length >= minPartialLength && y.includes(x)) return true
  if (y.length >= minPartialLength && x.includes(y)) return true
  return false
}

/**
 * Whether one entry claims one session.
 *
 * Only on the channel the administrator set for the entry's series, and only
 * where the session's series is one of the names the Series page records for
 * it. Nothing else: no default channel, no matching by car number, no ranking
 * of channels by what they carried. Each of those was tried and each produced
 * a list somebody had to be told how to read.
 */
export function entryClaimsSession(
  entry: EntryLike,
  session: Pick<SessionLike, 'id' | 'seriesNameRaw'>,
): boolean {
  if (!entry.channel || channelOf(session.id) !== entry.channel) return false
  return entryClaims(entry, session.seriesNameRaw)
}

/** Whether a configured series claims a session: its channel, one of its names. */
export function seriesClaimsSession(
  series: ConfiguredSeries,
  session: Pick<SessionLike, 'id' | 'seriesNameRaw'>,
): boolean {
  if (channelOf(session.id) !== series.channel || !session.seriesNameRaw) return false
  const target = normaliseSeries(session.seriesNameRaw)
  return series.names.some((n) => normaliseSeries(n) === target)
}

/** Whether one entry claims this session's series. */
export function entryClaims(entry: EntryLike, seriesNameRaw: string | undefined): boolean {
  if (!seriesNameRaw) return false

  // A confirmation is exact and exclusive. See EntryLike.seriesMatched.
  if (entry.seriesMatched?.length) {
    const target = normaliseSeries(seriesNameRaw)
    return entry.seriesMatched.some((m) => normaliseSeries(m) === target)
  }

  return entry.series ? seriesLooksSame(entry.series, seriesNameRaw) : false
}

export interface Partitioned<T> {
  /** Sessions of the series the team is entered in, on their channels. */
  ours: T[]
  /** Sessions of the event's other configured series, on theirs. */
  others: T[]
}

/**
 * Splits an event's sessions into ours, the other configured series', and
 * nothing.
 *
 * A session belonging to no configured series is not offered at all. The
 * capture sweeps every channel and keeps everything, which is right for
 * capture and wrong for a list: the same race arrives once per channel that
 * carried it, and the timetable scrape leaves placeholder buckets besides.
 */
export function partitionSessions<T extends SessionLike>(
  entries: readonly EntryLike[],
  sessions: readonly T[],
  otherSeries: readonly ConfiguredSeries[] = [],
): Partitioned<T> {
  const ours: T[] = []
  const others: T[] = []
  for (const s of sessions) {
    if (entries.some((e) => entryClaimsSession(e, s))) ours.push(s)
    else if (otherSeries.some((c) => seriesClaimsSession(c, s))) others.push(s)
  }
  return { ours, others }
}

/**
 * Why a team with entries here has no sessions of its own, when the event has
 * sessions at all.
 *
 * - `unconfigured`: an entry's series has no channel on this event, so it can
 *   claim nothing however the race runs. An administrator has to set one.
 * - `unnamed`: something is running on our channel that no configured series
 *   claims — most likely ours under a spelling the Series page does not have.
 * - `waiting`: everything is set up and nothing of ours has run yet. Another
 *   series practising first on a shared weekend is the ordinary case, and it
 *   needs nobody to do anything.
 *
 * A session on our channel that another configured series claims is that
 * series', not a misspelling of ours: Ligier JS Cup and Fun Cup share the root
 * channel at Zolder, and Ligier going out first is not a fault.
 */
export type NoneOursReason = 'unconfigured' | 'unnamed' | 'waiting'

export function whyNoneOurs(
  entries: readonly EntryLike[],
  sessions: readonly Pick<SessionLike, 'id' | 'seriesNameRaw'>[],
  otherSeries: readonly ConfiguredSeries[] = [],
): NoneOursReason {
  if (entries.some((e) => !e.channel)) return 'unconfigured'
  const channels = new Set(entries.map((e) => e.channel))
  const stray = sessions.some(
    (s) =>
      channels.has(channelOf(s.id)) &&
      !!s.seriesNameRaw &&
      !otherSeries.some((c) => seriesClaimsSession(c, s)),
  )
  return stray ? 'unnamed' : 'waiting'
}

// ------------------------------------------------------------- suggestions

export interface SeriesSuggestion {
  /** The stream's own string, exactly as stored, ready to confirm. */
  seriesNameRaw: string
  /** How many sessions in that series carried the number. */
  sessions: number
  /** How many carried it *and* nothing else did — the confident case. */
  exclusive: boolean
}

/**
 * Which stream series a number turned up in, most likely first.
 *
 * This is the evidence that makes confirming a series a click rather than a
 * guess. It is offered to a person; it never decides anything, because a
 * number appearing in two series is exactly the case it cannot resolve — and
 * that case is common enough to be the reason for the whole design.
 */
export function suggestSeries(
  number: string,
  sessions: readonly SessionLike[],
): SeriesSuggestion[] {
  const counts = new Map<string, number>()
  for (const s of sessions) {
    if (!s.seriesNameRaw || !s.cars?.includes(number)) continue
    counts.set(s.seriesNameRaw, (counts.get(s.seriesNameRaw) ?? 0) + 1)
  }
  const only = counts.size === 1
  return [...counts]
    .map(([seriesNameRaw, n]) => ({ seriesNameRaw, sessions: n, exclusive: only }))
    .sort((a, b) => b.sessions - a.sessions || a.seriesNameRaw.localeCompare(b.seriesNameRaw))
}

// ------------------------------------------------------------- the default

/**
 * How long a bucket may go unrefreshed before it stops counting as live.
 *
 * Capture restamps lastSeenAt every poll, so a quiet bucket means capture has
 * stopped rather than that the race has.
 */
export const liveWithinMs = 90_000

/**
 * How close two buckets' last sightings have to be to count as equally fresh.
 *
 * They tie constantly. One race carried on two channels produces two buckets
 * updated within a poll of each other, and a session that split when the stream
 * dropped its clock start produces two more. Ordering those by milliseconds is
 * a coin toss dressed as a decision.
 */
const tieWithinMs = 30_000

export interface ChooseOptions {
  nowMs: number
  /**
   * Prefer a session containing this number.
   *
   * What makes a team in two places at once work without a new concept: two
   * dashboards, each with its own car, each landing on its own race. Only ever
   * a tie-break among sessions already judged relevant — never an inclusion
   * rule, for the reasons in the module comment.
   */
  preferCar?: string | null
}

/**
 * Picks the session to open on: live if anything is, otherwise the most recent.
 *
 * "Most recent" alone was the tempting rule and it is wrong in the one case
 * that matters — the morning after a race, it shows yesterday's finished
 * session with the same confidence it shows a live one. The caller is expected
 * to say which it got; see describeFreshness.
 */
export function chooseSession<T extends SessionLike>(
  sessions: readonly T[],
  { nowMs, preferCar }: ChooseOptions,
): T | null {
  if (sessions.length === 0) return null

  const offered = sessions

  const live = offered.filter((s) => isLive(s, nowMs))
  const pool = live.length > 0 ? live : offered

  const withCar = preferCar ? pool.filter((s) => s.cars?.includes(preferCar)) : []
  const candidates = withCar.length > 0 ? withCar : pool

  const freshest = candidates.reduce(
    (best, s) => Math.max(best, s.lastSeenMs ?? 0),
    Number.NEGATIVE_INFINITY,
  )
  const tied = candidates.filter((s) => freshest - (s.lastSeenMs ?? 0) <= tieWithinMs)

  // Most complete wins among equals. When one race is carried on several
  // channels they are all "the session"; the one with the whole field is the
  // one worth opening, and the channel it came off is not a thing anybody
  // should be asked to choose between.
  return (
    [...tied].sort(
      (a, b) =>
        (b.cars?.length ?? 0) - (a.cars?.length ?? 0) ||
        // Same reasoning as orderSessions: among channels carrying the same
        // field, open the one that reports the most of what happens on it.
        (b.carsWithPitData ?? 0) - (a.carsWithPitData ?? 0) ||
        a.id.localeCompare(b.id),
    )[0] ?? null
  )
}

export interface SessionChoice<T> {
  ours: T[]
  others: T[]
  session: T | null
}

/**
 * Which session a view opens on, given everything that bears on it.
 *
 * One function because it was two copies. The member dashboard and the shared
 * view each hand-plumbed partitionSessions into chooseSession with the same
 * inputs, and adding the pinned channel meant remembering to do it twice.
 * They differ in exactly one decision, which is the parameter.
 *
 * `picked` is an explicit choice — a session id from the URL or the picker. It
 * reaches anything at the event, including sessions the filter would hide;
 * otherwise "show everything" would offer a row that could not then be opened.
 * What happens when it names nothing is where the two views part: a member who
 * just chose from a list should see the choice fail (`unresolved: 'nothing'`);
 * a shared link is bookmarked and reopened weeks later, when "nothing captured"
 * is a worse answer than this weekend's race (`unresolved: 'fallback'`).
 */
export function chooseFor<T extends SessionLike>(
  entries: readonly EntryLike[],
  sessions: readonly T[],
  opts: {
    nowMs: number
    preferCar?: string | null
    /** The event's other configured series, offered below ours. */
    otherSeries?: readonly ConfiguredSeries[]
    picked?: string | null
    unresolved: 'nothing' | 'fallback'
  },
): SessionChoice<T> {
  const { ours, others } = partitionSessions(entries, sessions, opts.otherSeries ?? [])
  const auto = () => chooseSession(ours, { nowMs: opts.nowMs, preferCar: opts.preferCar })

  let session: T | null
  if (opts.picked) {
    // Only what the list offers. A pick of a bucket no configured series
    // claims is an old choice, and opening it would bring back exactly the
    // rows this list no longer shows.
    const found = [...ours, ...others].find((s) => s.id === opts.picked) ?? null
    session = found ?? (opts.unresolved === 'fallback' ? auto() : null)
  } else {
    session = auto()
  }
  return { ours, others, session }
}

export function isLive(session: SessionLike, nowMs: number): boolean {
  return session.lastSeenMs != null && nowMs - session.lastSeenMs <= liveWithinMs
}

/**
 * The same question, asked of a session document rather than a candidate.
 *
 * `now` must be the BROWSER's clock, never ServerClock's. ServerClock derives
 * its offset from lastSeenAt precisely so that race times read correctly on a
 * viewer's wrong clock — which means that on a session captured in May it
 * reports a "now" in May, and lastSeenAt measured against it looks seconds old
 * for ever. A chart that asked ServerClock this question drew a fifteen-minute
 * stint as a thousand hours.
 */
export function isCaptureLive(lastSeenAt: Date | null | undefined, now: Date): boolean {
  return !!lastSeenAt && now.getTime() - lastSeenAt.getTime() <= liveWithinMs
}

/**
 * Says whether what is on screen is happening now or happened earlier.
 *
 * Not decoration. A dashboard showing a finished session looks exactly like one
 * showing a live session under a green flag, and the difference is the whole
 * question a person opening it is asking.
 */
export function describeFreshness(
  session: SessionLike | null,
  nowMs: number,
): { live: boolean; label: string } {
  if (!session || session.lastSeenMs == null) {
    return { live: false, label: 'No data yet' }
  }
  if (isLive(session, nowMs)) return { live: true, label: 'Live' }

  const seconds = Math.max(0, (nowMs - session.lastSeenMs) / 1000)
  const minutes = Math.round(seconds / 60)
  if (minutes < 90) return { live: false, label: `Ended ${minutes} min ago` }
  const hours = Math.round(minutes / 60)
  if (hours < 48) return { live: false, label: `Ended ${hours} hr ago` }
  return { live: false, label: `Ended ${Math.round(hours / 24)} days ago` }
}

/**
 * A stable order for showing sessions in a list.
 *
 * NOT the order the query returns. Firestore is asked for them by lastSeenAt
 * descending, which is right for finding the newest — but one race carried on
 * two channels produces two buckets that each restamp lastSeenAt about once a
 * second, so they leapfrog continuously and a dropdown left open reorders
 * itself every few seconds under the cursor.
 *
 * So freshness is quantised before it is compared: two sightings within the tie
 * window are the same answer, and the order is then decided by things that do
 * not move — how complete the bucket is, then its id. The same rule chooseSession
 * already applies for the same reason, applied here so the list a person reads
 * agrees with the choice the dashboard makes.
 */
export function orderSessions<T extends SessionLike>(sessions: readonly T[]): T[] {
  if (sessions.length === 0) return []
  const freshest = Math.max(...sessions.map((s) => s.lastSeenMs ?? 0))
  // A window measured from the freshest, not a fixed grid. Quantising by
  // division looks equivalent and is not: two buckets a millisecond apart can
  // straddle a boundary and swap every time the grid moves under them, which
  // is the same flicker in slower clothing.
  const tied = (s: T) => freshest - (s.lastSeenMs ?? 0) <= tieWithinMs

  return [...sessions].sort((a, b) => {
    const ta = tied(a)
    const tb = tied(b)
    if (ta !== tb) return ta ? -1 : 1
    if (!ta) {
      // Neither is live, so their timestamps have stopped moving and ordering
      // by them is stable.
      const byTime = (b.lastSeenMs ?? 0) - (a.lastSeenMs ?? 0)
      if (byTime !== 0) return byTime
    }
    const ca = a.cars?.length ?? 0
    const cb = b.cars?.length ?? 0
    if (ca !== cb) return cb - ca
    // Then by how much of the race each channel actually reports.
    const pa = a.carsWithPitData ?? 0
    const pb = b.carsWithPitData ?? 0
    if (pa !== pb) return pb - pa
    return a.id.localeCompare(b.id)
  })
}

/**
 * Which channel a bucket came off, read back out of its id.
 *
 * Needed because one race carried on two channels is two buckets, and the
 * vendor does not spell the series identically on both — "Ligier Js Cup" on one
 * and "Ligier JS Cup" on the other was the case that prompted this. Two list
 * entries differing only in capitalisation read as a bug in our code rather
 * than as two views of one race, so the list says which is which.
 */
export function channelOf(id: string): string | null {
  const [channel] = id.split('__')
  return channel && channel !== id ? channel : null
}
